import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Checkbox, Form, InputNumber, Select } from 'antd';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';

const { Option } = Select;

const pifuEligilibityChecklist = [
  'Is at low risk of urgent follow-up care and satisfies criteria established by the specialty',
  'Has the health literacy and knowledge, skills and confidence to manage their follow-up care (patient activation).',
  'Is confident and able to take responsibility for their care for the time they will be on the PIFU pathway',
  'Understands which changes in their symptoms or indicators mean they should get in touch with the service, and how to do so',
  'Has the tools they need to understand the status of their condition, and understands how to use them',
  'Understands how to book follow-up appointments directly with the service, and how long they will be responsible for doing this.'
];

const PIFUFields = ({
  appointment,
  isCompleted,
  pifuOutcomes,
  appointmentOutcome,
  user,
  appointmentMeta,
  pifuPifuSelfBook
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [waitForPifuLoad, setWaitForPifuLoad] = useState(false);
  const isDisabledSelfbookCheckbox = !pifuPifuSelfBook;

  const [pifuValidity, setPifuValidity] = useState(appointment.pifu_duration);
  const [pifuOutcome, setPifuOutcome] = useState(appointment.pifu_outcome_code);
  const [pifuSelfBook, setPifuSelfBook] = useState(appointment.pifu_self_book === 1);
  const [pifuEligilibity, setPifuEligibility] = useState(appointment.pifu_duration && appointment.pifu_outcome_code);

  const getSavedPIFUData = useCallback(async () => {
    const result = await dispatch(
      actions.clinicianDiagnosisAndOutcomePifuGet(user?.user_guid, appointment.appointment_id)
    );
    if (result) {
      setPifuValidity(result.duration);
      setPifuOutcome(result.pifu_outcome_code);
      setPifuSelfBook(result.self_book);
      setPifuEligibility(true);
    }
  }, [user?.user_guid, appointment.appointment_id, dispatch]);

  const checkPifuValidAndSave = useCallback(async () => {
    if (!pifuEligilibity || !pifuOutcome || !pifuValidity) return;
    const payload = {
      user_guid: appointment?.clinician_user_guid,
      appt_id: appointment?.appointment_id,
      duration: pifuValidity,
      self_book: pifuSelfBook,
      pifu_outcome_code: pifuOutcome
    };
    await dispatch(actions.clinicianDiagnosisAndOutcomePifuSave(payload));
  }, [pifuEligilibity, pifuOutcome, pifuSelfBook, pifuValidity]);

  useEffect(() => {
    const getData = async () => {
      if (
        appointmentMeta?.find((outcome) => outcome.appt_outcome_code === appointment.appointment_outcome_code)
          ?.appt_pifu_flag === 1
      ) {
        setWaitForPifuLoad(true);
        await getSavedPIFUData();
        setWaitForPifuLoad(false);
      }
    };
    getData();
  }, [appointmentMeta, appointment.appointment_outcome_code, getSavedPIFUData]);

  useEffect(() => {
    const save = async () => {
      await checkPifuValidAndSave();
    };

    save();
  }, [checkPifuValidAndSave]);

  return (
    <>
      {!waitForPifuLoad ? (
        <Card size="small" title="PIFU" style={{ marginBottom: 16 }} type="inner">
          {appointment?.case_review && (
            <Alert
              message={t('Please call the patient to inform them of the PIFU outcome.')}
              type="warning"
              style={{ margin: '8px 0px' }}
              showIcon
            />
          )}
          <Form.Item name="pifu_validity" label={t('PIFU Validity Length')} required labelCol={{ span: 10 }}>
            <InputNumber
              min={1}
              addonAfter="months"
              precision={0}
              value={pifuValidity}
              defaultValue={pifuValidity}
              onChange={(val) => {
                setPifuValidity(val);
              }}
              data-testid="pifu-validity"
            />
          </Form.Item>
          <Form.Item
            name="pifu_self_book"
            label={t('Allow patient to self book without contact centre')}
            labelCol={{ span: 10 }}
            tooltip={{
              title: isDisabledSelfbookCheckbox && 'Patient self-book is not available for this patient.',
              trigger: ['hover', 'focus']
            }}
          >
            <Checkbox
              onChange={(e) => {
                setPifuSelfBook(e.target.checked);
              }}
              defaultChecked={pifuSelfBook}
              checked={pifuSelfBook}
              disabled={isDisabledSelfbookCheckbox}
              data-testid="pifu-self-book"
            />
          </Form.Item>
          <Form.Item name="pifu_outcome" label={t('PIFU Expiry Outcome')} required labelCol={{ span: 10 }}>
            <Select
              disabled={isCompleted}
              placeholder={t('Select from dropdown')}
              value={pifuOutcome}
              defaultValue={pifuOutcome}
              onSelect={(value) => {
                setPifuOutcome(value);
              }}
              data-testid="pifu-outcome-select"
            >
              {pifuOutcomes?.map((res, index) => (
                <Option key={res.pifu_outcome_code} value={res.pifu_outcome_code}>
                  {res.pifu_outcome_desc}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {appointmentOutcome !== 'PIFU_EXTENSION' && (
            <Form.Item name="pifu_eligibility" label={t('PIFU Eligibility Checklist')} required labelCol={{ span: 10 }}>
              <Checkbox
                onChange={(e) => {
                  setPifuEligibility(e.target.checked);
                }}
                defaultChecked={pifuEligilibity}
                checked={pifuEligilibity}
                style={{ marginLeft: '0px' }}
              >
                {t('I confirm that this patient:')}
                <ul>
                  {pifuEligilibityChecklist.map((checkItem, index) => (
                    <li key={checkItem}>{checkItem}</li>
                  ))}
                </ul>
              </Checkbox>
            </Form.Item>
          )}
        </Card>
      ) : null}
    </>
  );
};

export default PIFUFields;
